// The one material a rig mesh wears when material_mapping binds nothing to it.
// PlayCanvas's `system.defaultMaterial` in our terms — see materialBinding.js
// for why an unbound mesh must NOT inherit another slot's material.
//
// UNLIT (MeshBasicMaterial) on purpose, and this is load-bearing rather than a
// shortcut. Scene.jsx mounts ambient and sun at intensity 0 and the key light
// only appears once an IBL applies, so a lit placeholder renders BLACK on any
// character with no baked environment — indistinguishable from a broken shader,
// which is the opposite of what a placeholder is for. Same reasoning, and the
// same fix, as the backdrop ground plane and the torch emitter cube.
//
// One shared instance for every unbound mesh: it holds no per-mesh state, and
// sharing keeps it out of the material-count/draw-call story. It is deliberately
// NOT registered in `_activeMaterials` — the tuning knobs, the envMap pass and
// the texture-quality cascade all operate on the character's authored materials,
// and this is not one of them.

import * as THREE from 'three'

// Neutral mid grey. Not magenta: an unassigned slot is a normal in-progress
// state while an artist builds a character, and the severity belongs in the
// console (bindingMessages), not in a viewport nobody can work in.
export const UNBOUND_COLOR = 0x9b9b9b

let _material = null

/** The shared placeholder. Created on first use so importing this module costs
 *  nothing on a character that binds every mesh. */
export function unboundMaterial() {
  if (!_material) {
    _material = new THREE.MeshBasicMaterial({ color: UNBOUND_COLOR })
    _material.name = 'aos-unbound-placeholder'
    // Read by anything that must not mistake the placeholder for an authored
    // material — the inspector's "displayed" column, a spec, a future exporter.
    _material.userData.aosUnbound = true
  }
  return _material
}

/** True for the placeholder — including after a scene round-trip, since the
 *  flag travels in userData rather than being an identity check on a module
 *  singleton that HMR can replace. */
export function isUnboundMaterial(material) {
  return !!material?.userData?.aosUnbound
}
